import { useState, useEffect, useCallback } from "react";

const API_BASE = process.env.REACT_APP_SNIPPIT_API_URL;

type TrackStatus = "pending" | "approved" | "rejected";

interface AdminTrack {
  id: number;
  csvTitle: string;
  csvArtist: string;
  matchedTitle: string | null;
  matchedArtist: string | null;
  artworkPath: string | null;
  deezerTrackId: number | null;
  confidenceScore: number | null;
  status: string;
  reviewNotes: string | null;
  chartDate: string;
  syncedAt: string;
  reviewedAt: string | null;
}

interface DeezerSearchResult {
  id: number;
  title: string;
  artist: string;
  artworkPath: string | null;
  preview: string | null;
}

interface AdminApi {
  apiKey: string;
  setApiKey: (key: string) => void;
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
}

function useAdminApi(): AdminApi {
  const [apiKey, setApiKey] = useState<string>(
    () => localStorage.getItem("admin_key") ?? "",
  );

  const get = useCallback(
    (path: string) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Admin-Key": apiKey,
      };
      return fetch(`${API_BASE}${path}`, { headers }).then((r) => {
        if (r.status === 401) throw new Error("unauthorized");
        return r.json();
      });
    },
    [apiKey],
  );

  const post = useCallback(
    (path: string, body?: unknown) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Admin-Key": apiKey,
      };
      return fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      }).then((r) => {
        if (r.status === 401) throw new Error("unauthorized");
        return r.json();
      });
    },
    [apiKey],
  );

  return { apiKey, setApiKey, get, post };
}

interface LoginGateProps {
  onLogin: (key: string) => void;
}

function LoginGate({ onLogin }: LoginGateProps) {
  const [key, setKey] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/tracks?status=pending`, {
        headers: { "X-Admin-Key": key },
      });
      if (res.status === 401) {
        setError(true);
        return;
      }
      localStorage.setItem("admin_key", key);
      onLogin(key);
    } catch {
      setError(true);
    }
  };

  return (
    <div className="admin-login-container">
      <h2 className="admin-login-title">admin access</h2>
      <input
        type="password"
        placeholder="api key"
        value={key}
        onChange={(e) => {
          setKey(e.target.value);
          setError(false);
        }}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        className={`admin-login-input ${error ? "admin-login-input-error" : ""}`}
      />
      {error && <p className="admin-login-error">invalid key</p>}
      <button className="admin-login-button" onClick={handleSubmit}>
        enter
      </button>
    </div>
  );
}

interface ConfidenceBadgeProps {
  score: number | null;
}

function ConfidenceBadge({ score }: ConfidenceBadgeProps) {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  const bgClass =
    score >= 0.7
      ? "badge-success"
      : score >= 0.4
        ? "badge-warning"
        : "badge-danger";
  return <span className={`confidence-badge ${bgClass}`}>{pct}% match</span>;
}

interface AudioPreviewProps {
  trackId: number | null;
}

function AudioPreview({ trackId }: AudioPreviewProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

   const load = async () => {
    if (url || !trackId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/tracks/${trackId}/audiopreview`, {
        headers: { "X-Admin-Key": localStorage.getItem("admin_key") ?? "" },
      });
      const data = await res.json();
      setUrl(data.previewUrl as string);
    } finally {
      setLoading(false);
    }
  };

  if (!trackId)
    return <span className="audio-preview-empty">no deezer id</span>;
  if (!url)
    return (
      <button className="audio-preview-button" onClick={load}>
        {loading ? "loading…" : "▶ load preview"}
      </button>
    );
  return <audio controls src={url} className="audio-preview-player" />;
}

interface OverrideSearchProps {
  trackId: number;
  post: AdminApi["post"];
  onOverride: (deezerTrackId: number) => void;
}

function OverrideSearch({ trackId, onOverride }: OverrideSearchProps) {
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<DeezerSearchResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [directId, setDirectId] = useState<string>("");
  const [directIdError, setDirectIdError] = useState<string | null>(null);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const data = await fetch(
        `${API_BASE}/api/admin/tracks/${trackId}/search?q=${encodeURIComponent(query)}`,
        { headers: { "X-Admin-Key": localStorage.getItem("admin_key") ?? "" } },
      ).then((r) => r.json() as Promise<DeezerSearchResult[]>);
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectIdSubmit = () => {
    setDirectIdError(null);
    const id = parseInt(directId.trim(), 10);
    if (isNaN(id) || id <= 0) {
      setDirectIdError("invalid track id");
      return;
    }
    onOverride(id);
    setDirectId("");
  };

  return (
    <div className="override-search-container">
      <div className="override-search-section">
        <p className="override-search-label">search deezer for correct track</p>
        <div className="override-search-input-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="title artist…"
            className="override-search-input"
          />
          <button className="override-search-button" onClick={search}>
            {loading ? "…" : "search"}
          </button>
        </div>
        {results && (
          <div className="override-search-results">
            {results.map((r) => (
              <div key={r.id} className="override-search-result-item">
                {r.artworkPath && (
                  <img
                    src={r.artworkPath}
                    alt=""
                    className="override-search-result-artwork"
                  />
                )}
                <div className="override-search-result-info">
                  <p className="override-search-result-title">{r.title}</p>
                  <p className="override-search-result-artist">{r.artist}</p>
                </div>
                {r.preview && (
                  <audio
                    controls
                    src={r.preview}
                    className="override-search-result-preview"
                  />
                )}
                <button
                  className="override-search-result-button"
                  onClick={() => onOverride(r.id)}
                >
                  use this
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="override-search-divider">or</div>

      <div className="override-search-section">
        <p className="override-search-label">enter deezer track id directly</p>
        <div className="override-search-input-row">
          <input
            value={directId}
            onChange={(e) => {
              setDirectId(e.target.value);
              setDirectIdError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleDirectIdSubmit()}
            placeholder="track id…"
            className={`override-search-input ${directIdError ? "override-search-input-error" : ""}`}
          />
          <button
            className="override-search-button"
            onClick={handleDirectIdSubmit}
          >
            use id
          </button>
        </div>
        {directIdError && (
          <p className="override-search-error">{directIdError}</p>
        )}
      </div>
    </div>
  );
}

interface TrackCardProps {
  track: AdminTrack;
  post: AdminApi["post"];
  onUpdate: (id: number, action: string) => void;
}

function TrackCard({ track, post, onUpdate }: TrackCardProps) {
  const [showOverride, setShowOverride] = useState<boolean>(false);
  const [acting, setActing] = useState<boolean>(false);

  const act = async (action: string, body?: unknown) => {
    setActing(true);
    try {
      await post(`/api/admin/tracks/${track.id}/${action}`, body);
      onUpdate(track.id, action);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="track-card">
      <div className="track-card-header">
        {track.artworkPath && (
          <img src={track.artworkPath} alt="" className="track-card-artwork" />
        )}
        <div className="track-card-info">
          <div className="track-card-title-row">
            <span className="track-card-title">
              {track.matchedTitle ?? track.csvTitle}
            </span>
            <ConfidenceBadge score={track.confidenceScore} />
          </div>
          <p className="track-card-artist">
            {track.matchedArtist ?? track.csvArtist}
          </p>
          <p className="track-card-meta">
            csv: <em>{track.csvTitle}</em> — <em>{track.csvArtist}</em> ·{" "}
            {track.chartDate}
          </p>
        </div>
      </div>

      <div className="track-card-preview">
        <AudioPreview trackId={track.deezerTrackId} />
      </div>

      <div className="track-card-actions">
        <button
          className="track-card-button track-card-button-approve"
          onClick={() => act("approve")}
          disabled={acting}
        >
          <i className="ti ti-check" aria-hidden="true" /> approve
        </button>
        <button
          className="track-card-button track-card-button-reject"
          onClick={() => act("reject")}
          disabled={acting}
        >
          <i className="ti ti-x" aria-hidden="true" /> reject
        </button>
        <button
          className="track-card-button track-card-button-override"
          onClick={() => setShowOverride((v) => !v)}
        >
          <i className="ti ti-edit" aria-hidden="true" /> override
        </button>
      </div>

      {showOverride && (
        <OverrideSearch
          trackId={track.id}
          post={post}
          onOverride={(deezerTrackId) => act("override", { deezerTrackId })}
        />
      )}
    </div>
  );
}

type TabStatus = TrackStatus | "preview";
interface StatusTabsProps {
  current: TabStatus;
  counts: Partial<Record<TabStatus, number>>;
  onChange: (status: TabStatus) => void;
}

function StatusTabs({ current, counts, onChange }: StatusTabsProps) {
  const tabs: TabStatus[] = ["pending", "approved", "rejected", "preview"];
  return (
    <div className="status-tabs">
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={`status-tab ${current === t ? "status-tab-active" : ""}`}
        >
          {t}{" "}
          {counts[t] != null ? (
            <span className="status-tab-count">({counts[t]})</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

interface UpcomingGame {
  gameDate: string;
  chartDate: string;
  tracks: AdminTrack[];
  pendingCount: number;
}

function UpcomingGames({ get }: { get: AdminApi["get"] }) {
  const [games, setGames] = useState<UpcomingGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    get<UpcomingGame[]>("/api/admin/preview?days=10")
      .then(setGames)
      .finally(() => setLoading(false));
  }, [get]);

  if (loading)
    return (
      <p className="admin-view-loading">loading upcoming games…</p>
    );

  return (
    <div className="upcoming-games-container">
      {games.map((g) => (
        <div key={g.gameDate} className="upcoming-game-item">
          <div
            className="upcoming-game-header"
            onClick={() =>
              setExpanded(expanded === g.gameDate ? null : g.gameDate)
            }
          >
            <div className="upcoming-game-header-left">
              <span className="upcoming-game-date">{g.gameDate}</span>
              <span className="upcoming-game-chart-date">chart: {g.chartDate}</span>
            </div>
            <div className="upcoming-game-header-right">
              {g.pendingCount > 0 && (
                <span className="upcoming-game-pending-badge">
                  {g.pendingCount} pending
                </span>
              )}
              <span className="upcoming-game-toggle">
                {expanded === g.gameDate ? "▲" : "▼"}
              </span>
            </div>
          </div>
          {expanded === g.gameDate && (
            <div className="upcoming-game-content">
              {g.tracks.map((t) => (
                <div key={t.id} className="upcoming-game-track">
                  {t.artworkPath && (
                    <img
                      src={t.artworkPath}
                      alt=""
                      className="upcoming-game-track-artwork"
                    />
                  )}
                  <div className="upcoming-game-track-info">
                    <p className="upcoming-game-track-title">
                      {t.matchedTitle ?? t.csvTitle}
                    </p>
                    <p className="upcoming-game-track-artist">
                      {t.matchedArtist ?? t.csvArtist}
                    </p>
                  </div>
                  <ConfidenceBadge score={t.confidenceScore} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminView() {
  const { setApiKey, get, post } = useAdminApi();
  const [authed, setAuthed] = useState<boolean>(
    !!localStorage.getItem("admin_key"),
  );
  const [status, setStatus] = useState<TabStatus>("pending");
  const [tracks, setTracks] = useState<AdminTrack[]>([]);
  const [counts, setCounts] = useState<Partial<Record<TrackStatus, number>>>(
    {},
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTracks = useCallback(
    async (s: TrackStatus) => {
      setLoading(true);
      setError(null);
      try {
        const data = await get<AdminTrack[]>(`/api/admin/tracks?status=${s}`);
        setTracks(data);
        setCounts((c) => ({ ...c, [s]: data.length }));
      } catch (e) {
        if (e instanceof Error && e.message === "unauthorized") {
          setAuthed(false);
          localStorage.removeItem("admin_key");
        } else {
          setError("failed to load tracks");
        }
      } finally {
        setLoading(false);
      }
    },
    [get],
  );

useEffect(() => {
  if (authed && status !== "preview") fetchTracks(status as TrackStatus);
}, [authed, status, fetchTracks]);

  const handleLogin = (key: string) => {
    setApiKey(key);
    setAuthed(true);
  };

  const handleUpdate = (id: number, _action: string) => {
    setTracks((prev) => prev.filter((t) => t.id !== id));
    setCounts((c) => ({ ...c, [status]: (c[status] ?? 1) - 1 }));
  };

  if (!authed) return <LoginGate onLogin={handleLogin} />;

  return (
    <div className="admin-view-container">
      <h2 className="admin-view-header">
        track review
        <button
          className="admin-view-signout"
          onClick={() => {
            localStorage.removeItem("admin_key");
            setAuthed(false);
          }}
        >
          sign out
        </button>
      </h2>
      <StatusTabs current={status} counts={counts} onChange={setStatus} />
      {status === "preview" ? (
        <UpcomingGames get={get} />
      ) : (
        <>
          {loading && <p className="admin-view-loading">loading…</p>}
          {error && <p className="admin-view-error">{error}</p>}
          {!loading && !error && tracks.length === 0 && (
            <p className="admin-view-empty">no {status} tracks</p>
          )}
          <div className="admin-view-tracks">
            {tracks.map((t) => (
              <TrackCard
                key={t.id}
                track={t}
                post={post}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
